import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type {
  PortalContentRow,
  PortalContentWriteInput,
} from "@colab/shared";

export type UpdatePortalContentInput = {
  id: string;
  input: PortalContentWriteInput;
};

// PATCH /api/admin/portal-content/:id
//
// Pessimistic. Invalidates portalContent.adminList + portalContent.detail(id)
// + portalContent.publicAll.
export function useUpdatePortalContent() {
  const queryClient = useQueryClient();
  return useMutation<
    { id: string; row: PortalContentRow },
    ApiClientError,
    UpdatePortalContentInput
  >({
    mutationFn: ({ id, input }) =>
      apiFetch<{ id: string; row: PortalContentRow }>(
        `/admin/portal-content/${encodeURIComponent(id)}`,
        { method: "PATCH", body: JSON.stringify(input) },
      ),
    onError: (err) => {
      toast.error(err.message ?? "Failed to update portal content");
    },
    onSettled: (_data, _err, { id }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.portalContent.adminList(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.portalContent.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.portalContent.publicAll(),
      });
    },
  });
}
